<template>
  <div class="tw-relative tw-px-7 tw-pb-7 tw-w-full lg:tw-w-3/4">
    <WdsAlert v-if="!isPublished" info icon bordered class="tw-mb-3">
      <WdsStack direction-row gap-sm>
        {{ $t('This job is in draft and only visible to people in your company') }}
        <span class="tw-text-black">{{ $t('Make this job available to enable sharing') }}</span>
      </WdsStack>
    </WdsAlert>

    <Header
      :job-local-id="jobLocalId"
      :title="$t('Manage job')"
      :subtitle="name"
      :link-text="$t('Job overview')"
      :link-url="jobLinkUrl"
      :removed="isRemoved"
      :paused="isPaused"
      :published="isPublished"
      :available="isAvailable"
      :completed="isCompleted"
      show-beta-label
    />
    <div v-if="jobFormLoadingError" class="tw-mt-9">
      <ContentLoadingErrorNotice />
    </div>
    <div v-else>
      <JobForm
        ref="jobFormRefs"
        :loading="loading"
        :first-time-loading="firstTimeLoading"
        @update:active-tab-key="handleTabChange"
      />

      <template v-if="!firstTimeLoading">
        <FormFooterLinks class="tw-pt-3" @update:active-tab-key="handleTabChange" />

        <ActionBar :width="actionBarWidth">
          <div class="tw-flex tw-justify-between tw-items-center tw-w-full">
            <WdsStack direction-row gap-md align-center>
              <div v-show="jobStore.hasPersistedJob">
                <div class="tw-text-sm tw-font-medium">{{ $t('Last saved') }}</div>
                <div class="tw-text-sm tw-text-gray-700">{{ timestamp }}</div>
              </div>
              <div>
                <HeaderStatus
                  :removed="isRemoved"
                  :paused="isPaused"
                  :published="isPublished"
                  :available="isAvailable"
                  :completed="isCompleted"
                />
              </div>
            </WdsStack>
            <WdsStack v-if="isPublished && validationStore.hasErrors" direction-column>
              <div class="tw-text-sm tw-font-medium tw-text-red-500">
                {{ $t("We couldn't save your changes") }}
              </div>
              <div class="tw-text-sm tw-text-gray-700">
                {{ $t('Please review the form fields and save again') }}
              </div>
            </WdsStack>
            <WdsStack direction-row align-center gap-md>
              <WdsSpinner v-if="loading" indeterminate data-test="saving-spinner" />

              <div
                v-if="formIsDirty"
                class="tw-text-indigo-500 tw-font-semibold tw-text-sm tw-text-right tw-w-[80px]"
              >
                {{
                  $tc(
                    isPublished
                      ? '1 unpublished change | :count unpublished changes'
                      : '1 unsaved change | :count unsaved changes',
                    jobStore.totalChanges,
                  )
                }}
              </div>

              <WdsButton
                v-if="jobStore.hasPersistedJob"
                :key="`saveButton`"
                :primary="isPublished"
                :tertiary="!isPublished"
                :disabled="saveButtonDisabled"
                data-test="save-button"
                @click="handleSave"
              >
                {{ isPublished ? $t('Publish changes') : $t('Save draft') }}
              </WdsButton>

              <template v-if="!isPublished">
                <WdsButton
                  v-if="canBePublished"
                  success
                  :disabled="loading"
                  data-test="publish-job-button"
                  @click="handlePublish"
                >
                  {{ $t('Make available') }}
                </WdsButton>
                <WdsTooltip v-else>
                  <template #tooltip-content>
                    {{ $t('You need to fill out all the required fields first') }}
                  </template>
                  <WdsButton success :disabled="true" data-test="publish-job-button">
                    {{ $t('Make available') }}
                  </WdsButton>
                </WdsTooltip>
              </template>
            </WdsStack>
          </div>
        </ActionBar>
      </template>
    </div>
  </div>
</template>

<script setup lang="ts">
import moment from 'moment'
import { storeToRefs } from 'pinia'
import { computed, onBeforeMount, watch, ref, onMounted } from 'vue'
import { ApolloError } from '@apollo/client/core'
import { useMutation, useQuery } from '@vue/apollo-composable'
import { useEventListener } from '@vueuse/core'
import { WdsAlert, WdsButton, WdsSpinner, WdsStack, WdsTooltip } from '@worksome/wds-components'
import { useFragment } from '@/gql'
import { $t, $tc } from '@/app-bootstrapping/SetUpInternationalization'
import SilentNetworkError from '@/utils/notification/SilentNetworkError'
import { FormValidation } from '@/utils/formValidation'
import { NetworkError } from '@/utils/notification'
import { laravelNamedRoute } from '@/utils/laravelNamedRoutes'
import namedRoutes from '@/utils/laravelNamedRoutes.json'

import ActionBar from '@/components/ActionBar.vue'
import ContentLoadingErrorNotice from '@/components/ContentLoadingErrorNotice.vue'
import { useFieldableFormStore } from '@/components/Fieldable/store/useFieldableFormStore'
import { mapCustomFieldValueToCustomFieldValueInput } from '@/components/Fieldable/utils'
import { JOB_TAB } from '@/components/Job/constants'
import Header from '@/components/Job/Header.vue'
import HeaderStatus from '@/components/Job/HeaderStatus.vue'
import JobForm from '@/components/Job/Edit/Form.vue'
import FormFooterLinks from '@/components/Job/Edit/FormFooterLinks.vue'
import {
  AttachJobToProjectMutation,
  DetachJobFromProjectMutation,
  SetInternalBudgetMutation,
  UpdateJobMutation,
} from '@/components/Job/graphql/mutations'
import { useJobFormStore } from '@/components/Job/Edit/store/jobFormStore'
import { GetJobFormQuery } from '@/components/Job/graphql/queries'
import { JobFormDataFragment } from '@/components/Job/graphql/fragments'
import { useValidationStore } from '@/components/Job/Edit/store/useValidationStore'

const url = new URL(window.location as any)

const props = defineProps<{
  jobId: string
  jobLocalId: number
  jobName: string
  jobLinkUrl: string
  jobIsRemoved: boolean
  jobIsPublished: boolean
  jobIsCompleted: boolean
  jobIsPaused: boolean
  jobIsAvailable: boolean
  companyId: string
  companyCurrency: string
  enableMultipleCurrencies: boolean
  companyRequireJobBudget: boolean
}>()

const jobFormRefs = ref<InstanceType<typeof JobForm> | null>()
const jobFormLoadingError = ref(false)

const jobStore = useJobFormStore()
const fieldableStore = useFieldableFormStore()
const validationStore = useValidationStore()

validationStore.setRequireJobBudget(props.companyRequireJobBudget)

const actionBarWidth = ref('100%')
const setActionBarWidth = () => {
  const offsetWidth = jobFormRefs.value?.tabsRef?.offsetWidth
  if (offsetWidth) {
    actionBarWidth.value = `${offsetWidth.toString()}px`
  }
}

watch(
  () => jobFormRefs.value?.tabsRef?.offsetWidth,
  (offsetWidth) => {
    if (offsetWidth) {
      setActionBarWidth()
    }
  },
  { deep: true },
)

onMounted(() => {
  useEventListener(window, 'resize', setActionBarWidth)
})

const { updateError } = validationStore

const {
  loading: jobLoading,
  onResult: onJobResult,
  onError: onJobLoadingError,
} = useQuery(
  GetJobFormQuery,
  {
    jobId: props.jobId,
  },
  { clientId: 'publicApi' },
)

onJobResult((queryJobResult) => {
  if (!queryJobResult.loading && queryJobResult.data && queryJobResult.data.job) {
    const job = useFragment(JobFormDataFragment, queryJobResult.data.job)
    jobStore.loadPersistedJob(job)
    fieldableStore.loadPersistedFieldValues(
      mapCustomFieldValueToCustomFieldValueInput(job.customFieldValues),
    )
  }
})

onJobLoadingError((error) => {
  jobFormLoadingError.value = true

  new SilentNetworkError({
    ex: error,
  }).dispatch()
})

const {
  loading: updateJobLoading,
  mutate: mutateUpdateJob,
  error: errorUpdateJob,
} = useMutation(UpdateJobMutation, {
  clientId: 'publicApi',
})

watch(errorUpdateJob, () => {
  updateError(errorUpdateJob)
  const { graphQLErrors } = errorUpdateJob.value as ApolloError

  if (FormValidation.containsFormValidations(graphQLErrors)) {
    validationStore.$externalResults = FormValidation.getSingularGraphqlErrors(
      graphQLErrors,
      'input.',
    )
  }
})

const { form } = storeToRefs(jobStore)

const { loading: updateInternalBudgetLoading, mutate: mutateUpdateInternalBudget } = useMutation(
  SetInternalBudgetMutation,
  {
    clientId: 'publicApi',
  },
)

const { loading: updateAttachJobToProjectLoading, mutate: mutateAttachJobToProject } = useMutation(
  AttachJobToProjectMutation,
  {
    clientId: 'publicApi',
  },
)

const { loading: updateDetachJobFromProjectLoading, mutate: mutateDetachJobFromProject } =
  useMutation(DetachJobFromProjectMutation, {
    clientId: 'publicApi',
  })

const { loading: publishJobIsLoading, mutate: mutatePublishJob } = useMutation(UpdateJobMutation, {
  clientId: 'publicApi',
})

const loading = computed(
  () =>
    updateJobLoading.value ||
    updateInternalBudgetLoading.value ||
    updateAttachJobToProjectLoading.value ||
    updateDetachJobFromProjectLoading.value ||
    publishJobIsLoading.value,
)

const name = computed(() => jobStore.form?.name || props.jobName)
const isRemoved = computed(() => jobStore.persistedJob?.removed || props.jobIsRemoved)
const isPublished = computed(() => jobStore.persistedJob?.published || props.jobIsPublished)
const isCompleted = computed(() => jobStore.persistedJob?.completed || props.jobIsCompleted)
const isPaused = computed(() => jobStore.persistedJob?.paused || props.jobIsPaused)
const isAvailable = computed(() => jobStore.persistedJob?.available || props.jobIsAvailable)

const firstTimeLoading = computed(() => jobLoading.value)

const formIsDirty = computed(() => jobStore.formIsDirty || fieldableStore.formIsDirty)

const timestamp = computed(() =>
  jobStore.hasPersistedJob ? moment(jobStore.persistedJob!.updatedAt).fromNow() : undefined,
)

const saveButtonDisabled = computed(() => {
  if (loading.value || (isPublished.value && !formIsDirty.value)) {
    return true
  }

  return (
    (!isPublished.value && !formIsDirty.value) ||
    (isPublished.value && formIsDirty.value && validationStore.hasErrors)
  )
})

const canBePublished = computed(() => !validationStore.customVuelidate.v$.$invalid)

const changeTab = (tab: JOB_TAB) => {
  url.searchParams.set('tab', tab)
  window.history.pushState({}, '', url)
  jobStore.activeTabKey = tab
}

async function updateJob(): Promise<boolean> {
  if (
    form.value.internalBudget &&
    form.value.internalBudget !== (jobStore.persistedJob?.internalBudget ?? '')
  ) {
    await mutateUpdateInternalBudget({
      input: {
        job: props.jobId,
        amount: Number(form.value.internalBudget),
      },
    })
  }

  if (form.value.project?.id && form.value.project.id !== jobStore.persistedJob?.project?.id) {
    await mutateAttachJobToProject({
      input: {
        jobs: [props.jobId],
        project: form.value.project.id,
      },
    })
  } else if (!form.value.project?.id && jobStore.persistedJob?.project?.id) {
    await mutateDetachJobFromProject({
      input: {
        job: props.jobId,
        project: jobStore.persistedJob.project.id,
      },
    })
  }

  const result = await mutateUpdateJob({
    input: jobStore.getFormInputPayload(),
  })
  jobStore.resetSubmitTrigger()
  validationStore.customVuelidate.v$.$reset()
  fieldableStore.v$.$reset()

  const data = result?.data?.updateJob
  if (data) {
    const job = useFragment(JobFormDataFragment, data)
    jobStore.loadPersistedJob(job)
    fieldableStore.loadPersistedFieldValues(
      mapCustomFieldValueToCustomFieldValueInput(job.customFieldValues),
    )

    return true
  }

  return false
}

const handleTabChange = async (tab: JOB_TAB) => {
  changeTab(tab)
}

const validateForm = () => {
  validationStore.$externalResults = {}

  validationStore.customVuelidate.v$.$reset()
  fieldableStore.v$.$reset()

  validationStore.customVuelidate.v$.$touch()
  fieldableStore.v$.$touch()
}

const handleSave = async () => {
  jobStore.setSubmitTrigger('save-button')

  if (isPublished.value) {
    validateForm()
  }

  if (
    (formIsDirty.value && !isPublished.value) ||
    (formIsDirty.value && isPublished.value && !validationStore.hasErrors)
  ) {
    try {
      await updateJob()
    } catch (ex: any) {
      new NetworkError({ ex, msg: $t('There was a problem while saving the job') }).dispatch()
    }
  }
}

const handlePublish = async () => {
  jobStore.setSubmitTrigger('publish-button')

  validateForm()

  if (!validationStore.hasErrors) {
    try {
      if (
        (formIsDirty.value && !isPublished.value) ||
        (formIsDirty.value && isPublished.value && !validationStore.hasErrors)
      ) {
        await updateJob()
      }

      const result = await mutatePublishJob({
        input: {
          id: jobStore.persistedJob!.id,
          published: true,
        },
      })

      const data = result?.data?.updateJob
      jobStore.resetSubmitTrigger()

      if (data) {
        const job = useFragment(JobFormDataFragment, data)

        jobStore.loadPersistedJob(job)

        fieldableStore.loadPersistedFieldValues(
          mapCustomFieldValueToCustomFieldValueInput(job.customFieldValues),
        )

        window.location.href = laravelNamedRoute(namedRoutes.jobs.candidates, {
          job: jobStore.persistedJob!.id,
        })
      }
    } catch (ex: any) {
      new NetworkError({
        ex,
        msg: $t('There was a problem while making the job published'),
      }).dispatch()
    }
  }
}

onBeforeMount(() => {
  const activeTab = Object.keys(JOB_TAB).find((key) => JOB_TAB[key] === url.searchParams.get('tab'))

  jobStore.activeTabKey = activeTab ? JOB_TAB[activeTab] : JOB_TAB.OVERVIEW
})
</script>
